/**
 * Arrow routing modes for annotation connectors.
 *
 * - `'straight'` — direct line from source to target
 * - `'orthogonal'` — auto-selects horizontal-first or vertical-first based on axis deltas
 * - `'horizontal-first'` — L-shape: horizontal leg then vertical leg
 * - `'vertical-first'` — L-shape: vertical leg then horizontal leg
 */
export type ArrowRouting = 'orthogonal' | 'straight' | 'horizontal-first' | 'vertical-first';

/**
 * Compute waypoints for an arrow route between two points.
 *
 * Returns an array of [x, y] waypoints:
 * - 2 waypoints for straight lines (or when points are axis-aligned / coincident)
 * - 3 waypoints for elbow connectors (source → elbow → target)
 *
 * @param source - Start point [x, y]
 * @param target - End point [x, y]
 * @param routing - Routing mode (default: 'orthogonal')
 * @returns Array of [x, y] waypoints
 */
export function computeArrowRoute(
  source: [number, number],
  target: [number, number],
  routing: ArrowRouting = 'orthogonal',
): Array<[number, number]> {
  const dx = Math.abs(target[0] - source[0]);
  const dy = Math.abs(target[1] - source[1]);

  // Axis-aligned or coincident → always straight
  if (dx < 1e-9 || dy < 1e-9) {
    return [source, target];
  }

  if (routing === 'straight') {
    return [source, target];
  }

  if (routing === 'horizontal-first') {
    return [source, [target[0], source[1]], target];
  }

  if (routing === 'vertical-first') {
    return [source, [source[0], target[1]], target];
  }

  // 'orthogonal' — auto-select based on which axis delta is larger
  if (dx >= dy) {
    // Horizontal first
    return [source, [target[0], source[1]], target];
  } else {
    // Vertical first
    return [source, [source[0], target[1]], target];
  }
}
